import { TemplateResult, html, css, CSSResultGroup } from 'lit';
import { OutlineElement } from '../../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './sic-secondary-menu.css.lit';
import componentVariables from './css-variables/vars-default.css.lit';
import { ResizeController } from '../../../controllers/resize-controller';
import { screenSizeMap } from '../../../controllers/screen-size-controller';
import { SlotsNGController as SlotController } from '../../../controllers/slots-ng-controller';
import '../../sic-container/sic-container';

/**
 * The secondary menu component
 *
 * @element sic-secondary-menu
 * @since 1.0.0
 * @slot - default slot for link items
 * @prop {String} breakpoint - The breakpoint at which the secondary menu will be visible.
 * @cssprop --sic-secondary-menu--default--display: The default display value for the secondary menu.
 * @cssprop --sic-secondary-menu--default--flex-direction: The default flex-direction value for the secondary menu.
 * @cssprop --sic-secondary-menu--default--justify-content: The default justify-content value for the secondary menu.
 * @cssprop --sic-secondary-menu--default--align-items: The default align-items value for the secondary menu.
 * @cssprop --sic-secondary-menu--default--align-content: The default align-content value for the secondary menu.
 * @cssprop --sic-secondary-menu--default--flex-wrap: The default flex-wrap value for the secondary menu.
 * @cssprop --sic-secondary-menu--default--gap: The default gap value for the secondary menu.
 * @cssprop --sic-secondary-menu--default--padding: The default padding value for the secondary menu.
 * @cssprop --sic-secondary-menu--default--margin: The default margin value for the secondary menu.
 * @cssprop --sic-secondary-menu-item--default--padding: The default padding value for the secondary menu.
 * @cssprop --sic-secondary-menu-item--default--margin: The default margin value for the secondary menu.
 */
@customElement('sic-secondary-menu')
export class SicSecondaryMenu extends OutlineElement {
  static styles: CSSResultGroup = [
    OutlineElement.styles,
    componentVariables,
    componentStyles,
    css``,
  ];
  slots = new SlotController(this);
  resizeController: ResizeController;
  // The resizeController is initialized here because visibleBreakpoint is not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    this.resizeController = new ResizeController(this, {
      breakpoints: [screenSizeMap.get(this.visibleBreakpoint) as number],
      containerQuery: false,
    });
  }

  /**
   * The breakpoint at which the secondary menu will be visible.
   *
   * This is done as a variable to ensure flexibility for future usages.
   */
  @property({ type: String, attribute: 'breakpoint' })
  visibleBreakpoint = 'lg';

  render(): TemplateResult {
    const isMobile = this.resizeController.currentBreakpointRange === 0;
    return isMobile
      ? html``
      : html` <nav
          class="sticky-secondary-nav"
          aria-labelledby="secondary_menu_label"
        >
          <div class="visually-hidden" id="secondary_menu_label">
            Secondary Menu
          </div>
          ${this.slots.conditionalSlot('', true, 'secondary-menu')}
        </nav>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-secondary-menu': SicSecondaryMenu;
  }
}
